import type { MixBoardSettings } from "@/features/settings/stores/mixStore";
import type { SettingsTabId } from "@/features/settings/utils/settingsTabs";
import type { TypedEvent } from "@/shared/lib/componentHierarchy";
import type { ForceDisplayMode } from "@/shared/lib/forcePc";

export type SettingsEvent =
  | {
      readonly type: "settings/tab-selected";
      readonly tabId: SettingsTabId;
    }
  | {
      readonly type: "settings/tab-key-pressed";
      readonly key: string;
    }
  | {
      readonly type: "settings/modal-visibility-changed";
      readonly isOpen: boolean;
    }
  | {
      readonly type: "settings/oekaki-tool-changed";
      readonly value: string;
    }
  | {
      readonly type: "settings/speech-boolean-changed";
      readonly field: "alwaysEnabled" | "autoScroll";
      readonly value: boolean;
    }
  | {
      readonly type: "settings/speech-number-changed";
      readonly field: "port" | "rate" | "volume";
      readonly value: string;
    }
  | {
      readonly type: "settings/speech-reset";
    }
  | {
      readonly type: "settings/board-selection-synced";
      readonly boardSettings: MixBoardSettings;
      readonly registeredSlugs: readonly string[];
    }
  | {
      readonly type: "settings/board-selection-toggled";
      readonly slug: string;
      readonly enabled: boolean;
    }
  | {
      readonly type: "settings/board-selection-all-toggled";
    }
  | {
      readonly type: "settings/board-help-toggled";
      readonly slug: string;
      readonly alignRight: boolean;
    }
  | {
      readonly type: "settings/board-help-dismissed";
    }
  | {
      readonly type: "settings/mix-board-share-changed";
      readonly slug: string;
      readonly value: string;
    }
  | {
      readonly type: "settings/favorite-draft-changed";
      readonly field: "word" | "regex" | "word2" | "regex2";
      readonly value: string;
    }
  | {
      readonly type: "settings/favorite-add-requested";
      readonly field: "word" | "regex" | "word2" | "regex2";
    }
  | {
      readonly type: "settings/favorite-remove-requested";
      readonly field: "word" | "regex" | "word2" | "regex2";
      readonly value: string;
    }
  | {
      readonly type: "settings/ng-draft-changed";
      readonly field:
        | "displayId"
        | "title"
        | "word"
        | "regex"
        | "image"
        | "imageNote";
      readonly value: string;
    }
  | {
      readonly type: "settings/ng-add-requested";
      readonly field: "displayId" | "title" | "word" | "regex" | "image";
    }
  | {
      readonly type: "settings/ng-remove-requested";
      readonly field:
        | "displayId"
        | "title"
        | "word"
        | "regex"
        | "image"
        | "hiddenThread";
      readonly value: string | number;
    }
  | {
      readonly type: "settings/ng-clear-images-requested";
    }
  | {
      readonly type: "settings/ng-image-add-completed";
      readonly value: string;
      readonly note: string;
      readonly success: boolean;
      readonly message: string;
    }
  | {
      readonly type: "settings/ng-boolean-changed";
      readonly field: "enabled" | "showNgContent";
      readonly value: boolean;
    }
  | {
      readonly type: "settings/display-value-changed";
      readonly key: string;
      readonly value: boolean | number | string | null;
    }
  | {
      readonly type: "settings/display-mode-changed";
      readonly value: ForceDisplayMode;
    }
  | {
      readonly type: "settings/display-dialog-changed";
      readonly dialog: "clear-history" | "clear-all-data";
      readonly open: boolean;
    }
  | {
      readonly type: "settings/display-dialog-confirmed";
      readonly dialog: "clear-history" | "clear-all-data";
    };

export function isSettingsEvent(event: TypedEvent): event is SettingsEvent {
  return (
    event.type === "settings/tab-selected" ||
    event.type === "settings/tab-key-pressed" ||
    event.type === "settings/modal-visibility-changed" ||
    event.type === "settings/oekaki-tool-changed" ||
    event.type === "settings/speech-boolean-changed" ||
    event.type === "settings/speech-number-changed" ||
    event.type === "settings/speech-reset" ||
    event.type === "settings/board-selection-synced" ||
    event.type === "settings/board-selection-toggled" ||
    event.type === "settings/board-selection-all-toggled" ||
    event.type === "settings/board-help-toggled" ||
    event.type === "settings/board-help-dismissed" ||
    event.type === "settings/mix-board-share-changed" ||
    event.type === "settings/favorite-draft-changed" ||
    event.type === "settings/favorite-add-requested" ||
    event.type === "settings/favorite-remove-requested" ||
    event.type === "settings/ng-draft-changed" ||
    event.type === "settings/ng-add-requested" ||
    event.type === "settings/ng-remove-requested" ||
    event.type === "settings/ng-clear-images-requested" ||
    event.type === "settings/ng-image-add-completed" ||
    event.type === "settings/ng-boolean-changed" ||
    event.type === "settings/display-value-changed" ||
    event.type === "settings/display-mode-changed" ||
    event.type === "settings/display-dialog-changed" ||
    event.type === "settings/display-dialog-confirmed"
  );
}
